/**
 * ============================================================================
 * Bundle Rewards Progress Tracker - EVENT-DRIVEN VERSION
 * ============================================================================
 *
 * REFACTORED: Now uses cart:update events instead of independent API calls
 *
 * Tracks customer progress toward bundle discounts for "mitt" products.
 * Displays a visual progress bar with pill-shaped milestones showing:
 * - Current cart status (how many mitts are in cart)
 * - Preview of what happens if they add current product
 * - Dynamic discount tiers with automatic rewards
 *
 * Bundle Tiers:
 * - 1 mitt: Entry level, no discount
 * - 2 mitts: 10% discount applied
 * - 3+ mitts: 15% discount + FREE face mitt
 * ============================================================================
 */

(function () {
  'use strict';

  /* Configuration & Bundle Tiers */
  const STORAGE_KEY = 'folia-progress-state';

  const MITT_TIERS = {
    tier1: {
      min: 1,
      discount: 0,
      label: 'YOUR BUNDLE & SAVE REWARDS',
    },
    tier2: {
      min: 2,
      discount: 10,
      label: '10% DISCOUNT APPLIED!',
    },
    tier3: {
      min: 3,
      discount: 15,
      label: '15% OFF + FREE FACE MITT!',
    },
  };

  /* State Management */
  let currentMittCount = 0;

  /* Cart Data Functions - Now takes cart object instead of fetching */
  function countMittsFromCart(cart) {
    if (!cart || !cart.items) return 0;

    return cart.items.reduce((total, item) => {
      if (item.title && item.title.toLowerCase().includes('mitt')) {
        return total + item.quantity;
      }
      return total;
    }, 0);
  }

  /* Location-Based Text Updates */
  function updateShippingText() {
    const countryCode = window.Shopify?.country || '';
    const country = window.ThemeGlobals?.getCountryName?.(countryCode) || '';
    const isAustralia = countryCode === 'AU' || country === 'Australia';

    const shippingType = isAustralia ? 'Postage' : 'Shipping';
    const fullShippingText = country ? `${country} ${shippingType}` : shippingType;

    document.querySelectorAll('[data-shipping-full]').forEach((span) => {
      span.textContent = fullShippingText;
    });

    document.querySelectorAll('[data-shipping-type]').forEach((span) => {
      span.textContent = shippingType;
    });
  }

  /* Cart Drawer Progress Bar Updates */
  function updateCartDrawerProgress(mittCount) {
    // Use requestAnimationFrame to ensure DOM has updated after morphSection
    requestAnimationFrame(() => {
      const container = document.querySelector('.cart-progress-container');
      const statusBadge = document.querySelector('.cart-progress-status');
      const progressMeter = document.querySelector('.progress-bar-meter');
      const pills = document.querySelectorAll('.cart-drawer .milestone-pill');

      if (!container || !pills.length) return;

      // Add subtle pulse animation
      container.classList.add('updating');
      setTimeout(() => container.classList.remove('updating'), 600);

      // Remove active class from ALL pills
      pills.forEach((pill) => pill.classList.remove('active'));
      container.classList.remove('discount-active');

      let statusText = MITT_TIERS.tier1.label;

      // Re-query pills to get fresh references after morphSection
      const pill1 = document.querySelector('.cart-drawer .milestone-pill[data-step="1"]');
      const pill2 = document.querySelector('.cart-drawer .milestone-pill[data-step="2"]');
      const pill3 = document.querySelector('.cart-drawer .milestone-pill[data-step="3"]');

      if (mittCount >= 1 && pill1) {
        pill1.classList.add('active');
      }

      if (mittCount >= 2 && pill2) {
        pill2.classList.add('active');
        container.classList.add('discount-active');
        statusText = MITT_TIERS.tier2.label;
      }

      if (mittCount >= 3 && pill3) {
        pill3.classList.add('active');
        statusText = MITT_TIERS.tier3.label;
      }

      if (progressMeter) {
        const track = document.querySelector('.cart-drawer .progress-bar-track');
        if (track) {
          const trackWidth = track.offsetWidth;

          if (mittCount >= 3) {
            progressMeter.style.width = trackWidth + 'px';
          } else if (mittCount >= 2) {
            progressMeter.style.width = trackWidth * 0.5 + 'px';
          } else {
            progressMeter.style.width = '0px';
          }
        } else {
          if (mittCount >= 3) {
            progressMeter.style.width = '100%';
          } else if (mittCount >= 2) {
            progressMeter.style.width = '50%';
          } else {
            progressMeter.style.width = '0%';
          }
        }
      }

      if (statusBadge) {
        statusBadge.textContent = statusText;
      }
    });
  }

  /* Product Page Progress Preview */
  function updateProductPageProgress(mittCount, previewCount = null) {
    const progressWrapper = document.querySelector('[data-product-progress]');
    if (!progressWrapper) return;

    const pills = progressWrapper.querySelectorAll('.milestone-pill');
    const progressMeter = progressWrapper.querySelector('[data-progress-fill]');
    const progressTrack = progressWrapper.querySelector('.progress-bar-track');
    const previewMeter = progressWrapper.querySelector('[data-preview-fill]');

    if (!pills.length) return;

    requestAnimationFrame(() => {
      if (progressTrack && pills.length >= 3) {
        const firstPill = pills[0];
        const lastPill = pills[pills.length - 1];
        const progressBar = firstPill.closest('.progress-bar');

        if (progressBar) {
          const containerRect = progressBar.getBoundingClientRect();
          const firstPillRect = firstPill.getBoundingClientRect();
          const lastPillRect = lastPill.getBoundingClientRect();

          const trackStart = firstPillRect.left - containerRect.left + firstPillRect.width / 2;
          const trackEnd = lastPillRect.left - containerRect.left + lastPillRect.width / 2;
          const trackWidth = trackEnd - trackStart;

          progressTrack.style.left = trackStart + 'px';
          progressTrack.style.width = trackWidth + 'px';
          progressTrack.style.right = 'auto';

          if (progressMeter) {
            progressMeter.style.left = trackStart + 'px';
          }
          if (previewMeter) {
            previewMeter.style.left = trackStart + 'px';
          }
        }
      }
    });

    pills.forEach((pill) => {
      pill.classList.remove('active', 'preview-active');
    });

    if (mittCount >= 1) {
      const pill1 = progressWrapper.querySelector('.milestone-pill[data-step="1"]');
      if (pill1) pill1.classList.add('active');
    }
    if (mittCount >= 2) {
      const pill2 = progressWrapper.querySelector('.milestone-pill[data-step="2"]');
      if (pill2) pill2.classList.add('active');
    }
    if (mittCount >= 3) {
      const pill3 = progressWrapper.querySelector('.milestone-pill[data-step="3"]');
      if (pill3) pill3.classList.add('active');
    }

    requestAnimationFrame(() => {
      if (progressMeter && progressTrack) {
        const trackWidth = parseFloat(progressTrack.style.width) || 0;

        if (mittCount >= 3) {
          progressMeter.style.width = trackWidth + 'px';
        } else if (mittCount >= 2) {
          progressMeter.style.width = trackWidth * 0.5 + 'px';
        } else {
          progressMeter.style.width = '0px';
        }
      }
    });

    if (previewCount !== null && previewCount > mittCount) {
      if (previewCount >= 1 && mittCount < 1) {
        const pill1 = progressWrapper.querySelector('.milestone-pill[data-step="1"]');
        if (pill1) pill1.classList.add('preview-active');
      }
      if (previewCount >= 2 && mittCount < 2) {
        const pill2 = progressWrapper.querySelector('.milestone-pill[data-step="2"]');
        if (pill2) pill2.classList.add('preview-active');
      }
      if (previewCount >= 3 && mittCount < 3) {
        const pill3 = progressWrapper.querySelector('.milestone-pill[data-step="3"]');
        if (pill3) pill3.classList.add('preview-active');
      }

      requestAnimationFrame(() => {
        if (previewMeter && progressTrack) {
          const trackWidth = parseFloat(progressTrack.style.width) || 0;

          if (previewCount >= 3) {
            previewMeter.style.width = trackWidth + 'px';
          } else if (previewCount >= 2) {
            previewMeter.style.width = trackWidth * 0.5 + 'px';
          } else {
            previewMeter.style.width = '0px';
          }
        }
      });
    } else if (previewMeter) {
      previewMeter.style.width = '0';
    }
  }

  /* Product Page Helpers */
  function getProductQuantity() {
    const qtyInput = document.querySelector('input[name="quantity"]');
    if (!qtyInput) return 1;

    const productTitle = document.querySelector('h1, .product__title')?.textContent || '';
    const isMitt = productTitle.toLowerCase().includes('mitt');

    return isMitt ? parseInt(qtyInput.value) || 1 : 0;
  }

  /* Main Update Function - Now takes cart data instead of fetching */
  function updateAllProgress(cart) {
    const mittCount = countMittsFromCart(cart);

    if (mittCount === currentMittCount) return;

    currentMittCount = mittCount;

    updateShippingText();
    updateCartDrawerProgress(mittCount);

    const productQty = getProductQuantity();
    updateProductPageProgress(mittCount, mittCount + productQty);

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        count: mittCount,
        timestamp: Date.now(),
      })
    );
  }

  /* State Persistence */
  function applyStoredState() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const { count } = JSON.parse(stored);
      currentMittCount = count;

      updateCartDrawerProgress(count);
      updateProductPageProgress(count, count + getProductQuantity());
    } catch (error) {
      console.error('Error applying stored state:', error);
    }
  }

  /* Event Listener Setup */
  function setupProductPageListeners() {
    const qtyInput = document.querySelector('input[name="quantity"]');
    if (!qtyInput) return;

    ['input', 'change'].forEach((event) => {
      qtyInput.addEventListener(event, () => {
        const productQty = getProductQuantity();
        updateProductPageProgress(currentMittCount, currentMittCount + productQty);
      });
    });

    document.addEventListener('click', (e) => {
      if (
        e.target.closest('[data-quantity-button], .quantity__button, button[name="plus"], button[name="minus"]')
      ) {
        setTimeout(() => {
          const productQty = getProductQuantity();
          updateProductPageProgress(currentMittCount, currentMittCount + productQty);
        }, 50);
      }
    });
  }

  /**
   * EVENT HANDLER - Listens to cart:update events
   */
  function handleCartUpdate(event) {
    const cart = event.detail.resource;

    if (cart && cart.items) {
      updateAllProgress(cart);
    } else {
      // Fallback: fetch cart if not in event
      fetchCartAndUpdate();
    }
  }

  /**
   * FALLBACK - Only used for initial page load
   */
  async function fetchCartAndUpdate() {
    try {
      const response = await fetch('/cart.js', {
        cache: 'no-store',
      });

      if (!response.ok) throw new Error('Failed to fetch cart');

      const cart = await response.json();
      updateAllProgress(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  /* Initialization */
  function init() {
    applyStoredState();
    setupProductPageListeners();

    // Initial fetch on page load
    fetchCartAndUpdate();

    // Listen to cart:update events (PRIMARY DATA SOURCE)
    document.addEventListener('cart:update', handleCartUpdate);

    // Also listen to cart:add events
    document.addEventListener('cart:add', handleCartUpdate);

    window.addEventListener('resize', () => {
      const productQty = getProductQuantity();
      updateProductPageProgress(currentMittCount, currentMittCount + productQty);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose function for cart-upgrade.js to call
  window.updateAllProgress = (cart) => updateAllProgress(cart);
})();
